import type { DashboardWidgetDefinition, DashboardWidgetKey } from "@repo/shared/types";
import { Fragment, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  DragHandleCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDragReorder } from "@/hooks/use-drag-reorder";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { SizeGlyph } from "./size-glyph";
import { useWidgetText } from "./use-widget-text";
import { T } from "./widget-table.i18n";

type WidgetTableProps = {
  /** The widgets of this zone that are switched on, in dashboard order. */
  shown: DashboardWidgetKey[];
  /** Every widget of this zone the dashboard can show, in registry order. */
  available: DashboardWidgetDefinition[];
  onReorder: (shown: DashboardWidgetKey[]) => Promise<unknown>;
  onToggle: (key: DashboardWidgetKey, on: boolean) => void;
  /** Extra content under a switched-on widget's row (the cash threshold). */
  renderBelow?: (key: DashboardWidgetKey) => ReactNode;
};

const COLUMN_COUNT = 5;

/**
 * One zone's widgets: the switched-on ones first, in dashboard order and
 * draggable, then the rest under "Not shown". A switch or a drop saves at once.
 */
export function WidgetTable({
  shown,
  available,
  onReorder,
  onToggle,
  renderBelow,
}: WidgetTableProps) {
  const t = useT(T);
  const widgetText = useWidgetText();
  const { ordered, getRowProps } = useDragReorder({
    items: shown,
    onCommit: async (next) => {
      await onReorder(next);
    },
  });
  const byKey = new Map(available.map((w) => [w.key, w]));
  const hidden = available.filter((w) => !shown.includes(w.key));

  const row = (key: DashboardWidgetKey, on: boolean, index?: number) => {
    const widget = byKey.get(key);
    if (!widget) return null;
    const text = widgetText(key);
    const below = on ? renderBelow?.(key) : null;
    return (
      <Fragment key={key}>
        <TableRow {...(on && index !== undefined ? getRowProps(index) : {})}>
          {on ? <DragHandleCell /> : <TableCell className="w-12" />}
          <TableCell>
            <div className="flex items-center gap-2">
              <span className={cn("font-medium", !on && "text-muted-foreground")}>{text.name}</span>
              {widget.requiredPermission === "compliance" && (
                <Badge variant="info">{t.complianceOnly}</Badge>
              )}
            </div>
          </TableCell>
          <TableCell className="text-muted-foreground">{text.shows}</TableCell>
          <TableCell>
            <SizeGlyph size={widget.size} label={widget.size === "full" ? t.full : widget.size} />
          </TableCell>
          <TableCell className="text-right">
            <Switch
              checked={on}
              onCheckedChange={(checked) => onToggle(key, checked)}
              aria-label={text.name}
            />
          </TableCell>
        </TableRow>
        {below && (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT} className="pt-0 pl-16!">
              {below}
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  };

  return (
    <Table variant="data">
      <TableHeader>
        <TableRow>
          <TableHead className="w-12" />
          <TableHead>{t.colWidget}</TableHead>
          <TableHead>{t.colShows}</TableHead>
          <TableHead className="w-32">{t.colSize}</TableHead>
          <TableHead className="w-24 text-right">{t.colShow}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {ordered.map((key, index) => row(key, true, index))}
        {hidden.length > 0 && (
          <TableRow>
            <TableCell
              colSpan={COLUMN_COUNT}
              className="bg-muted/40 py-2 pl-16! text-xs font-medium text-muted-foreground"
            >
              {t.notShown}
            </TableCell>
          </TableRow>
        )}
        {hidden.map((w) => row(w.key, false))}
      </TableBody>
    </Table>
  );
}
